/* https://gomakethings.com/html-templates-with-vanilla-javascript/ */

import { module as Module } from 'modujs';
import loadProject from '../constants/project-loadmore';
import gsap from 'gsap';
import loadNews from '../constants/news-loadmore';

export default class Loadmore extends Module {
    static waitingDuration = 1200;

    constructor(m) {
        super(m);

        // Binding
        this.onFetch = this.onFetch.bind(this);

        // UI
        this.$list = this.$('list')[0];
        this.$template = this.$('template')[0];
        this.$skeleton = this.$('skeleton')[0];

        // Data
        this.apiRoute = location.origin + this.getData('api');
        this.total = parseInt(this.getData('total'));
        this.currentPage = parseInt(this.getData('current'));
        this.isLoading = false;
        this.type = this.getData('type');
        this.complete = false;
        this.way = null;
        this.animation = null;
        this.nextUrl = this.getData('next');

        // Fill function
        switch (this.type) {
            case 'project':
                this.fillFunction = loadProject;
                break;
            case 'article':
                this.fillFunction = loadNews;
                break;
        }
    }

    // =============================================================================
    // Lifecycle
    // =============================================================================
    init() {}

    destroy() {
        super.destroy();
    }

    // =============================================================================
    // Callbacks
    // =============================================================================
    onTrigger({ way }) {
        this.way = way;
        this.load();
    }

    onFetch(json, url) {
        const $items = [];

        // Set current page
        this.currentPage = json.pagination.currentPage;

        // Remove flag
        this.isLoading = false;

        // If no more items to fetch
        if (this.currentPage === this.total) {
            // Destroy module
            this.destroy();
            // Complete
            this.complete = true;
        }

        gsap.delayedCall(Loadmore.waitingDuration / 1000, () => {
            const $animatedItems = [];

            for (let index = 0; index < json.items.length; index++) {
                const itemData = json.items[index];
                $items.push(this.fillFunction(this.$template, itemData));
            }

            // Append items
            $items.forEach(($item) => {
                this.call('addScrollElements', $item, 'Scroll');

                $item.firstElementChild.style.opacity = 0;
                $animatedItems.push($item.firstElementChild);
                this.$list.appendChild($item);
            });

            // Remove loading class
            this.el.classList.remove('is-loading-more');

            // Hide skeleton
            this.animation && this.animation.kill();
            this.$skeleton.style = '';

            // Add page params in url
            /* if (this.nextUrl) {
                this.call('replaceUrl', this.nextUrl, 'Load')
                this.nextUrl = json.pagination.nextUrl;
            } */

            // Appear
            gsap.fromTo(
                $animatedItems,
                {
                    opacity: 0,
                    y: 40,
                    scale: 1.025
                },
                {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    stagger: 0.1,
                    duration: 0.3,
                    onComplete: () => {
                        // Load next
                        if (this.way === 'leave' && json.pagination.remainingItems > 0) {
                            this.load();
                        }
                    }
                }
            );
        });
    }

    // =============================================================================
    // Methods
    // =============================================================================
    getFetchUrl() {
        const url = new URL(this.apiRoute);

        url.searchParams.delete('page');

        const params = {
            page: this.currentPage + 1
        };

        const addSearchParams = (url, params = {}) =>
            new URL(
                `${url.origin}${url.pathname}?${new URLSearchParams([
                    ...Array.from(url.searchParams.entries()),
                    ...Object.entries(params)
                ])}`
            );

        const { href } = addSearchParams(url, params);

        return href;
    }

    async fetchJson(url, callback) {
        this.controller = new AbortController();
        const signal = this.controller.signal;

        try {
            const response = await fetch(url, { signal });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            } else {
                const json = await response.json();
                callback(json, url);
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log(`Fetch request was aborted for ${url}`);
                callback(null);
            } else {
                console.error('Fetch error: ', error);
                callback(null);

                requestAnimationFrame(() => {
                    this.isLoading = false;

                    // Remove loading class
                    this.el.classList.remove('is-loading-more');

                    // Hide skeleton
                    this.animation && this.animation.kill();
                    this.$skeleton.style = '';
                });
            }
        }
    }

    load() {
        if (this.way === 'leave' && !this.isLoading && !this.complete) {
            this.isLoading = true;

            // Add Loading class
            this.el.classList.add('is-loading-more');

            // Show skeleton
            this.animation && this.animation.kill();

            requestAnimationFrame(() => {
                this.animation = gsap.to(this.$skeleton, {
                    autoAlpha: 1,
                    display: 'block',
                    duration: (Loadmore.waitingDuration / 1000) * 0.3
                });
                // Fetch
                this.fetchJson(this.getFetchUrl(), this.onFetch);
            });
        }
    }
}
